import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import React, { useEffect, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

const EDITOR_STYLES = `
  .wysiwyg-wrapper .public-DraftStyleDefault-block {
    margin: 0;
    line-height: 1.6;
  }
  .wysiwyg-wrapper .public-DraftStyleDefault-ltr {
    margin: 4px 0;
  }
  .wysiwyg-wrapper .DraftEditor-editorContainer {
    font-size: 14px;
    font-family: inherit;
  }
  .wysiwyg-wrapper figure {
    margin: 0;
  }
  .wysiwyg-wrapper .public-DraftStyleDefault-block {
    white-space: pre-wrap;
  }
`;

const normalizeIncomingHtmlForEditor = (html: string): string => {
    return html
        // Convert <p><br/></p> and <p><br></p> back to plain empty paragraphs
        // so html-to-draftjs doesn't create extra blocks from the <br> inside.
        .replace(/<p><br\s*\/?><\/p>/gi, "<p></p>")
        // Remove formatting-only whitespace/newlines between tags.
        .replace(/>\s+</g, "><")
        .trim();
};

const normalizeDraftHtmlForStorage = (html: string): string => {
    return html
        // Preserve consecutive spaces for display consistency.
        .replace(/  /g, " &nbsp;")
        // Remove formatting-only whitespace between tags (e.g. trailing \n after each </p>).
        .replace(/>\s+</g, "><")
        .trim();
};

const EditText = (props: { content: string; setContent: (value: string) => void }) => {
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const initialized = useRef(false);

    // Inject scoped CSS once
    useEffect(() => {
        const styleId = "edittext-wysiwyg-styles";
        if (!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.textContent = EDITOR_STYLES;
            document.head.appendChild(style);
        }
    }, []);

    // Initialize the editor ONCE when the saved HTML arrives from API.
    useEffect(() => {
        if (initialized.current) return;
        const incoming = normalizeIncomingHtmlForEditor(props.content || "");
        if (!incoming) return;

        try {
            const blocksFromHtml = htmlToDraft(incoming);
            if (blocksFromHtml) {
                const { contentBlocks, entityMap } = blocksFromHtml;
                const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
                setEditorState(EditorState.createWithContent(contentState));
            }
        } catch (_) {
            // leave empty if parsing fails
        }
        initialized.current = true;
    }, [props.content]);

    const handleEditorChange = (state: typeof editorState) => {
        // Mark initialized so the init-from-props effect never fires after user starts editing
        initialized.current = true;
        setEditorState(state);
        const html = normalizeDraftHtmlForStorage(
            draftToHtml(convertToRaw(state.getCurrentContent()))
        );
        props.setContent(html);
    };

    return (
        <div className="wysiwyg-wrapper">
            <Editor
                toolbar={{
                    options: ["inline", "fontSize", "list", "textAlign", "link", "colorPicker"],
                    inline: {
                        options: ["bold", "italic", "underline"],
                    },
                    fontSize: {
                        options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                    },
                    textAlign: {
                        options: ["left", "center", "right", "justify"],
                    },
                    list: {
                        options: ["unordered", "ordered"],
                    },
                }}
                editorState={editorState}
                onEditorStateChange={handleEditorChange}
                editorStyle={{
                    minHeight: "200px",
                    border: "1px solid #E6E8F0",
                    padding: "8px 12px",
                    borderRadius: "0 0 4px 4px",
                    lineHeight: "1.6",
                    fontSize: "14px",
                }}
                toolbarStyle={{
                    borderRadius: "4px 4px 0 0",
                    border: "1px solid #E6E8F0",
                    borderBottom: "none",
                    marginBottom: 0,
                }}
            />
        </div>
    );
};

export default EditText;
